// src/pages/DirectorioCorreos.jsx
// Directorio de correos DreamHost cruzado con el padrón del intranet. Solo para
// personal del Desk. Permite revisar el cruce (aceptar candidato / marcar sin
// match / ignorar) y marcar buzones para baja. La baja se ejecuta a mano en el
// panel de DreamHost; aquí solo se registra la solicitud.
import React, { useEffect, useState, useCallback, useContext } from 'react';
import { Table, Button, Spinner, Form, Row, Col, InputGroup, Badge, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AuthContext } from '../context/AuthContext';
import { MorphIcon } from 'morphicons/react';
import {
    listMailboxes, getResumen, getDominios,
    confirmar, marcarSinMatch, cuentaArea, reabrir, solicitarBaja,
    getAccess, setAccess,
    guardarPassword, borrarPassword, revelarPassword,
    getConfig, setCookie, syncAhora,
} from '../services/mailDirectoryApi';

// Piloto morphicons: candado que se abre al revelar la contraseña. Formato
// IconInput (array [tag, attrs], estilo Lucide). Solo cambia el arco (shackle).
const LOCK_CLOSED = [['rect', { x: 5, y: 11, width: 14, height: 9, rx: 2 }], ['path', { d: 'M8.5 11V8a3.5 3.5 0 0 1 7 0v3' }]];
const LOCK_OPEN = [['rect', { x: 5, y: 11, width: 14, height: 9, rx: 2 }], ['path', { d: 'M8.5 11V8a3.5 3.5 0 0 1 7 0' }]];

const CARD  = { background: 'var(--c-card)', borderRadius: 12, border: '1px solid var(--c-border)', boxShadow: '0 1px 4px rgba(0,0,0,.06)' };
const LABEL = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--c-text-4)', margin: 0 };

const CLAS_LABEL = {
    match_fuerte: 'Match fuerte', candidatos: 'Candidatos',
    sin_match: 'Sin match', cuenta_funcional: 'Cuenta funcional',
};
const CLAS_COLOR = {
    match_fuerte: '#10b981', candidatos: '#f59e0b',
    sin_match: '#6b7280', cuenta_funcional: '#8b5cf6',
};
const DEC_LABEL = {
    pendiente: 'Pendiente', confirmado: 'Confirmado',
    sin_match: 'Sin match', cuenta_area: 'Cuenta de área',
};

// Set de iconos propio, en SVG (mismo patrón que el resto del Desk). Nada de emojis.
const Ico = ({ children, size = 15, stroke = 1.7 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>{children}</svg>
);
const I = {
    lock:   <Ico><rect x="5" y="10.5" width="14" height="9" rx="2" /><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" /></Ico>,
    trash:  <Ico><path d="M5 7h14M10 7V5.5a1.5 1.5 0 0 1 1.5-1.5h1A1.5 1.5 0 0 1 14 5.5V7M6.7 7l.8 11a1.5 1.5 0 0 0 1.5 1.4h6a1.5 1.5 0 0 0 1.5-1.4L17.3 7" /></Ico>,
    undo:   <Ico><path d="M4 9h9.5a5 5 0 0 1 0 10H8" /><path d="M8 5 4 9l4 4" /></Ico>,
    x:      <Ico><path d="M6.5 6.5l11 11M17.5 6.5l-11 11" /></Ico>,
    area:   <Ico><rect x="5" y="4" width="14" height="16" rx="1.5" /><path d="M9 8h2M13 8h2M9 12h2M13 12h2M10 20v-3h4v3" /></Ico>,
    refresh:<Ico><path d="M20 11a8 8 0 0 0-14-4.9L4 8" /><path d="M4 13a8 8 0 0 0 14 4.9L20 16" /><path d="M4 4v4h4M20 20v-4h-4" /></Ico>,
    link:   <Ico><path d="M9.5 14.5l5-5" /><path d="M13 7l1.3-1.3a3.3 3.3 0 0 1 4.7 4.7L17 12" /><path d="M11 17l-1.3 1.3a3.3 3.3 0 0 1-4.7-4.7L7 12" /></Ico>,
    users:  <Ico><circle cx="9.5" cy="9" r="3.2" /><path d="M3.5 19c.5-3.2 3-4.9 6-4.9s5.5 1.7 6 4.9" /><path d="M16 6.4a3.2 3.2 0 0 1 0 6.2M17.5 14.6c2.1.5 3.4 2.1 3.8 4.4" /></Ico>,
    eye:    <Ico><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" /><circle cx="12" cy="12" r="2.8" /></Ico>,
    check:  <Ico><path d="M5 12.5l4.5 4.5L19 7" /></Ico>,
};
// Icono + texto dentro de un botón, alineados.
const BtnIco = ({ icon, children }) => (
    <span className="d-inline-flex align-items-center gap-1">{icon}{children}</span>
);

function Pill({ text, color, bg }) {
    return (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: 20,
            background: bg || '#f3f4f6', color: color || '#6b7280',
            fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap',
        }}>{text}</span>
    );
}

function Kpi({ label, value, tone, onClick, active }) {
    return (
        <div
            onClick={onClick}
            title={onClick ? 'Filtrar' : undefined}
            style={{
                ...CARD, padding: '12px 16px', minWidth: 130,
                cursor: onClick ? 'pointer' : 'default',
                borderColor: active ? '#3b82f6' : 'var(--c-border)',
                boxShadow: active ? '0 0 0 2px rgba(59,130,246,.25)' : CARD.boxShadow,
            }}
        >
            <p style={LABEL}>{label}</p>
            <div style={{ fontSize: 24, fontWeight: 800, color: tone || 'var(--c-text-1)' }}>{value}</div>
        </div>
    );
}

export default function DirectorioCorreos() {
    const { user } = useContext(AuthContext);
    const esAdmin = user?.role === 'admin';
    const [showAccess, setShowAccess] = useState(false);
    const [showConexion, setShowConexion] = useState(false);
    const [passwordDe, setPasswordDe] = useState(null); // buzón cuyo modal de clave está abierto

    const [items, setItems]       = useState([]);
    const [total, setTotal]       = useState(0);
    const [page, setPage]         = useState(1);
    const [size]                  = useState(50);
    const [loading, setLoading]   = useState(false);
    const [resumen, setResumen]   = useState(null);
    const [dominios, setDominios] = useState([]);

    // filtros
    const [search, setSearch]               = useState('');
    const [searchDeb, setSearchDeb]         = useState('');
    const [domain, setDomain]               = useState('');
    const [clasificacion, setClasificacion] = useState('');
    const [decision, setDecision]           = useState('');
    const [soloBajas, setSoloBajas]         = useState(false);
    const [soloBajaSol, setSoloBajaSol]     = useState(false);
    const [soloBajaEjec, setSoloBajaEjec]   = useState(false);
    const [incluirFuncionales, setIncluirFuncionales] = useState(false);

    // limpia todos los filtros (card "Buzones")
    const limpiarFiltros = () => {
        setSearch(''); setDomain(''); setClasificacion(''); setDecision(''); setSoloBajas(false); setSoloBajaSol(false); setSoloBajaEjec(false); setIncluirFuncionales(false);
    };
    const sinFiltros = !searchDeb && !domain && !clasificacion && !decision && !soloBajas && !soloBajaSol && !soloBajaEjec && !incluirFuncionales;

    useEffect(() => {
        getResumen().then(setResumen).catch(() => {});
        getDominios().then(setDominios).catch(() => {});
    }, []);

    // debounce de búsqueda
    useEffect(() => {
        const t = setTimeout(() => setSearchDeb(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    // al cambiar filtros, vuelve a página 1
    useEffect(() => { setPage(1); }, [searchDeb, domain, clasificacion, decision, soloBajas, soloBajaSol, soloBajaEjec, incluirFuncionales]);

    const cargar = useCallback(() => {
        setLoading(true);
        const params = { page, size };
        if (searchDeb.trim()) params.search = searchDeb.trim();
        if (domain) params.domain = domain;
        if (clasificacion) params.clasificacion = clasificacion;
        if (incluirFuncionales) params.incluirFuncionales = 'true';
        if (decision) params.decision = decision;
        if (soloBajas) params.soloBajas = 'true';
        if (soloBajaSol) params.bajaSolicitada = 'true';
        if (soloBajaEjec) params.bajaEjecutada = 'true';
        listMailboxes(params)
            .then((r) => { setItems(r.items); setTotal(r.total); })
            .catch((e) => toast.error(e.response?.data?.error || 'No se pudo cargar el directorio'))
            .finally(() => setLoading(false));
    }, [page, size, searchDeb, domain, clasificacion, decision, soloBajas, soloBajaSol, soloBajaEjec, incluirFuncionales]);

    useEffect(() => { cargar(); }, [cargar]);

    const refrescarResumen = () => getResumen().then(setResumen).catch(() => {});

    // Dispara la actualización: lanza el runner desacoplado (extrae de DreamHost
    // si hay cookie + cruza contra el padrón). Responde al toque; el resultado
    // llega en ~3 min, así que refrescamos la vista poco después.
    const actualizarAhora = async () => {
        try {
            await syncAhora();
            toast.info('Actualización lanzada. El directorio se refresca en ~3 minutos.');
            setTimeout(() => { refrescarResumen(); cargar(); }, 3.5 * 60 * 1000);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo lanzar la actualización');
        }
    };

    // actualiza una fila en memoria tras una acción
    const reemplazar = (mb) => setItems((prev) => prev.map((x) => (x.email === mb.email ? mb : x)));

    const accion = async (fn, email, ...args) => {
        try {
            const mb = await fn(email, ...args);
            reemplazar(mb);
            refrescarResumen();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Acción fallida');
        }
    };

    const totalPaginas = Math.max(1, Math.ceil(total / size));

    return (
        <div style={{ padding: '20px 24px', maxWidth: 1400, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ fontWeight: 800, margin: 0 }}>Directorio de correos</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {resumen?.ultimoSync && (
                        <span style={{ fontSize: 12, color: 'var(--c-text-4)' }}>
                            Último sync: {new Date(resumen.ultimoSync).toLocaleString('es-MX')}
                        </span>
                    )}
                    {esAdmin && (
                        <>
                            <Button size="sm" variant="outline-primary" onClick={actualizarAhora}>
                                <BtnIco icon={I.refresh}>Actualizar</BtnIco>
                            </Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => setShowConexion(true)}>
                                <BtnIco icon={I.link}>Conexión DreamHost</BtnIco>
                            </Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => setShowAccess(true)}>
                                <BtnIco icon={I.users}>Gestionar acceso</BtnIco>
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {esAdmin && showAccess && <AccessModal onClose={() => setShowAccess(false)} />}
            {esAdmin && showConexion && <ConexionModal onClose={() => setShowConexion(false)} />}
            {passwordDe && (
                <PasswordModal
                    mb={passwordDe}
                    esAdmin={esAdmin}
                    onClose={() => setPasswordDe(null)}
                    onSaved={(mb) => { reemplazar(mb); setPasswordDe(null); }}
                />
            )}

            {/* KPIs */}
            {resumen && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '16px 0' }}>
                    <Kpi label="Buzones" value={resumen.total} active={sinFiltros} onClick={limpiarFiltros} />
                    <Kpi label="Pendientes" value={resumen.decision?.pendiente || 0} tone="#f59e0b"
                        active={decision === 'pendiente'}
                        onClick={() => { setSoloBajas(false); setSoloBajaSol(false); setDecision(decision === 'pendiente' ? '' : 'pendiente'); }} />
                    <Kpi label="Confirmados" value={resumen.decision?.confirmado || 0} tone="#10b981"
                        active={decision === 'confirmado'}
                        onClick={() => { setSoloBajas(false); setSoloBajaSol(false); setDecision(decision === 'confirmado' ? '' : 'confirmado'); }} />
                    <Kpi label="Bajas detectadas" value={resumen.bajasDetectadas || 0} tone="#ef4444"
                        active={soloBajas}
                        onClick={() => { setDecision(''); setSoloBajaSol(false); setSoloBajas((v) => !v); }} />
                    <Kpi label="Baja solicitada" value={resumen.bajaSolicitada || 0} tone="#ef4444"
                        active={soloBajaSol}
                        onClick={() => { setSoloBajas(false); setSoloBajaEjec(false); setSoloBajaSol((v) => !v); }} />
                    <Kpi label="Baja ejecutada" value={resumen.bajaEjecutada || 0} tone="#10b981"
                        active={soloBajaEjec}
                        onClick={() => { setSoloBajas(false); setSoloBajaSol(false); setSoloBajaEjec((v) => !v); }} />
                </div>
            )}

            {/* Filtros */}
            <div style={{ ...CARD, padding: 12, marginBottom: 16 }}>
                <Row className="g-2 align-items-center">
                    <Col md={4}>
                        <InputGroup>
                            <Form.Control
                                placeholder="Buscar correo, nombre de buzón o empleado…"
                                value={search} onChange={(e) => setSearch(e.target.value)}
                            />
                            {search && <Button variant="outline-secondary" onClick={() => setSearch('')}>×</Button>}
                        </InputGroup>
                    </Col>
                    <Col md={3}>
                        <Form.Select value={domain} onChange={(e) => setDomain(e.target.value)}>
                            <option value="">Todos los dominios</option>
                            {dominios.map((d) => <option key={d.domain} value={d.domain}>{d.domain} ({d.n})</option>)}
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Form.Select value={clasificacion} onChange={(e) => setClasificacion(e.target.value)}>
                            <option value="">Toda clasificación</option>
                            {Object.entries(CLAS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Form.Select value={decision} onChange={(e) => setDecision(e.target.value)}>
                            <option value="">Toda decisión</option>
                            {Object.entries(DEC_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </Form.Select>
                    </Col>
                    <Col md={1}>
                        <Form.Check
                            type="switch" label="Bajas" checked={soloBajas}
                            onChange={(e) => setSoloBajas(e.target.checked)}
                            title="Solo buzones confirmados cuyo empleado está en baja"
                        />
                    </Col>
                </Row>
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 10, flexWrap: 'wrap' }}>
                    <Form.Check
                        type="switch" id="sw-baja-solicitada" label="Con baja solicitada"
                        checked={soloBajaSol}
                        onChange={(e) => { setSoloBajaSol(e.target.checked); if (e.target.checked) setSoloBajas(false); }}
                        title="Solo los buzones que ya marcaste para baja"
                    />
                    <Form.Check
                        type="switch" id="sw-funcionales" label="Incluir cuentas de área"
                        checked={incluirFuncionales}
                        onChange={(e) => setIncluirFuncionales(e.target.checked)}
                        title="Muestra también admin, direccion, ventas y otros buzones que no son de una persona"
                    />
                    {resumen?.cuentasFuncionales > 0 && !incluirFuncionales && (
                        <span style={{ fontSize: 12, color: 'var(--c-text-4)' }}>
                            {resumen.cuentasFuncionales} cuentas de área ocultas
                        </span>
                    )}
                </div>
            </div>

            {/* Tabla */}
            <div style={{ ...CARD, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center' }}><Spinner animation="border" size="sm" /> Cargando…</div>
                ) : items.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--c-text-4)' }}>Sin resultados con estos filtros.</div>
                ) : (
                    <Table hover responsive className="mb-0" style={{ fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: 'var(--c-bg-2)' }}>
                                <th>Correo</th>
                                <th>Cruce</th>
                                <th>Empleado</th>
                                <th>Estado</th>
                                <th style={{ width: 260 }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((mb) => <Fila key={mb.email} mb={mb} accion={accion} onPassword={setPasswordDe} />)}
                        </tbody>
                    </Table>
                )}
            </div>

            {/* Paginación */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                <span style={{ fontSize: 12, color: 'var(--c-text-4)' }}>{total} buzones · página {page} de {totalPaginas}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
                    <Button size="sm" variant="outline-secondary" disabled={page >= totalPaginas} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
                </div>
            </div>
        </div>
    );
}

// Panel de admin: lista blanca de quién puede entrar al módulo.
function AccessModal({ onClose }) {
    const [users, setUsers]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy]       = useState(null);
    const [q, setQ]             = useState('');

    useEffect(() => {
        getAccess()
            .then(setUsers)
            .catch((e) => toast.error(e.response?.data?.error || 'No se pudo cargar la lista'))
            .finally(() => setLoading(false));
    }, []);

    const toggle = async (u) => {
        if (u.implicito) return; // admin: acceso implícito, no editable
        setBusy(u.id);
        try {
            const r = await setAccess(u.id, !u.acceso);
            setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, acceso: r.acceso } : x)));
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo cambiar el acceso');
        } finally {
            setBusy(null);
        }
    };

    const filtrados = users.filter((u) =>
        !q.trim() || (u.name + ' ' + u.username).toLowerCase().includes(q.trim().toLowerCase()));
    const conAcceso = users.filter((u) => u.acceso).length;

    return (
        <Modal show onHide={onClose} size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 18 }}>Acceso al Directorio de correos</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p style={{ fontSize: 13, color: 'var(--c-text-4)' }}>
                    Marca quién puede ver y abrir el módulo. Los administradores siempre tienen acceso.
                    Actualmente <b>{conAcceso}</b> con acceso.
                </p>
                <InputGroup className="mb-3">
                    <Form.Control placeholder="Buscar por nombre o usuario…" value={q} onChange={(e) => setQ(e.target.value)} />
                </InputGroup>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 30 }}><Spinner animation="border" size="sm" /></div>
                ) : (
                    <Table hover responsive style={{ fontSize: 13 }}>
                        <thead><tr><th>Persona</th><th>Rol</th><th style={{ width: 110 }}>Acceso</th></tr></thead>
                        <tbody>
                            {filtrados.map((u) => (
                                <tr key={u.id}>
                                    <td><b>{u.name}</b><div style={{ fontSize: 11, color: 'var(--c-text-4)' }}>{u.username}</div></td>
                                    <td><Badge bg="light" text="dark">{u.role}</Badge></td>
                                    <td>
                                        <Form.Check
                                            type="switch"
                                            checked={u.acceso}
                                            disabled={u.implicito || busy === u.id}
                                            onChange={() => toggle(u)}
                                            label={u.implicito ? 'siempre' : ''}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
}

// Modal de contraseña de un buzón. Guarda cifrado en el server; revelar es admin.
function PasswordModal({ mb, esAdmin, onClose, onSaved }) {
    const [password, setPassword] = useState('');
    const [revelada, setRevelada] = useState(null);
    const [busy, setBusy] = useState(false);

    const guardar = async () => {
        if (!password) return;
        setBusy(true);
        try { onSaved(await guardarPassword(mb.email, password)); }
        catch (e) { toast.error(e.response?.data?.error || 'No se pudo guardar'); setBusy(false); }
    };
    const revelar = async () => {
        setBusy(true);
        try { const r = await revelarPassword(mb.email); setRevelada(r.password); }
        catch (e) { toast.error(e.response?.data?.error || 'No se pudo revelar'); }
        finally { setBusy(false); }
    };
    const borrar = async () => {
        setBusy(true);
        try { onSaved(await borrarPassword(mb.email)); }
        catch (e) { toast.error(e.response?.data?.error || 'No se pudo borrar'); setBusy(false); }
    };

    return (
        <Modal show onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16 }}>
                    <span className="d-inline-flex align-items-center gap-2">
                        <MorphIcon icon={revelada != null ? LOCK_OPEN : LOCK_CLOSED} size={18} color="currentColor" strokeWidth={1.7} />
                        Contraseña de {mb.email}
                    </span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p style={{ fontSize: 13, color: 'var(--c-text-4)' }}>
                    Se guarda cifrada en el servidor. La API nunca la devuelve salvo este “revelar”.
                </p>
                {mb.tienePassword && (
                    <div style={{ marginBottom: 12 }}>
                        <Pill text="Contraseña guardada" color="#10b981" bg="#ecfdf5" />
                        {esAdmin && (
                            <div style={{ marginTop: 8 }}>
                                {revelada == null ? (
                                    <Button size="sm" variant="outline-secondary" disabled={busy} onClick={revelar}><BtnIco icon={I.eye}>Revelar</BtnIco></Button>
                                ) : (
                                    <code style={{ background: 'var(--c-bg-2)', padding: '4px 8px', borderRadius: 6 }}>{revelada}</code>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>{mb.tienePassword ? 'Cambiar por' : 'Contraseña'}</Form.Label>
                <Form.Control type="text" value={password} placeholder="Escribe la contraseña"
                    onChange={(e) => setPassword(e.target.value)} />
            </Modal.Body>
            <Modal.Footer style={{ justifyContent: 'space-between' }}>
                <span>
                    {mb.tienePassword && (
                        <Button size="sm" variant="outline-danger" disabled={busy} onClick={borrar}>Borrar guardada</Button>
                    )}
                </span>
                <span>
                    <Button variant="secondary" onClick={onClose} className="me-2">Cerrar</Button>
                    <Button variant="primary" disabled={busy || !password} onClick={guardar}>Guardar</Button>
                </span>
            </Modal.Footer>
        </Modal>
    );
}

// Modal de conexión a DreamHost: pegar la cookie del panel y verificar la
// extracción automática. Solo admin.
function ConexionModal({ onClose }) {
    const [cfg, setCfg] = useState(null);
    const [cookie, setCookieInput] = useState('');
    const [busy, setBusy] = useState('');

    const cargar = () => getConfig().then(setCfg).catch(() => {});
    useEffect(() => { cargar(); }, []);

    const guardar = async () => {
        setBusy('guardando');
        try { await setCookie(cookie); setCookieInput(''); toast.success('Cookie guardada'); await cargar(); }
        catch (e) { toast.error(e.response?.data?.error || 'No se pudo guardar'); }
        finally { setBusy(''); }
    };
    const extraer = async () => {
        setBusy('extrayendo');
        try {
            await syncAhora();
            toast.info('Lanzado. En ~3 min verás aquí el estado de la cookie y la última extracción.');
            setTimeout(cargar, 3.5 * 60 * 1000);
        } catch (e) { toast.error(e.response?.data?.error || 'No se pudo lanzar'); }
        finally { setBusy(''); }
    };

    const estadoColor = { valida: '#10b981', expirada: '#ef4444', sin_configurar: '#6b7280' };

    return (
        <Modal show onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16 }}>Conexión a DreamHost</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!cfg ? <Spinner animation="border" size="sm" /> : (
                    <>
                        {!cfg.hayLlave && (
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
                                Falta <code>MAIL_DIR_SECRET_KEY</code> en el servidor: no se puede cifrar la cookie ni las contraseñas.
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, fontSize: 13 }}>
                            <div><b>Cookie:</b> <Pill text={cfg.cookieEstado} color={estadoColor[cfg.cookieEstado] || '#6b7280'} bg="#f3f4f6" /></div>
                            <div><b>Última extracción:</b> {cfg.ultimaExtraccion ? new Date(cfg.ultimaExtraccion).toLocaleString('es-MX') : '—'}
                                {cfg.ultimaExtraccion && (cfg.ultimaExtraccionOk ? ' ✓' : ` ✗ (${cfg.ultimaExtraccionDetalle || ''})`)}</div>
                        </div>
                        <p style={{ fontSize: 12, color: 'var(--c-text-4)' }}>
                            Pega aquí el header <b>Cookie</b> de una sesión del panel (DevTools → Network → petición a
                            panel.dreamhost.com → Request Headers → Cookie). Se guarda cifrada y la usa la extracción nocturna.
                        </p>
                        <Form.Control as="textarea" rows={3} value={cookie} placeholder="cookie del panel…"
                            onChange={(e) => setCookieInput(e.target.value)} style={{ fontFamily: 'monospace', fontSize: 12 }} />
                    </>
                )}
            </Modal.Body>
            <Modal.Footer style={{ justifyContent: 'space-between' }}>
                <Button variant="outline-primary" disabled={!!busy || !cfg} onClick={extraer}>
                    <BtnIco icon={I.refresh}>{busy === 'extrayendo' ? 'Lanzando…' : 'Extraer y sincronizar'}</BtnIco>
                </Button>
                <span>
                    <Button variant="secondary" onClick={onClose} className="me-2">Cerrar</Button>
                    <Button variant="primary" disabled={!!busy || cookie.length < 20} onClick={guardar}>
                        {busy === 'guardando' ? 'Guardando…' : 'Guardar cookie'}
                    </Button>
                </span>
            </Modal.Footer>
        </Modal>
    );
}

function Fila({ mb, accion, onPassword }) {
    const clasColor = CLAS_COLOR[mb.clasificacion] || '#6b7280';
    const empBaja = mb.decision === 'confirmado' && mb.empleado && mb.empleado.activo === false;

    return (
        <tr style={empBaja ? { background: 'rgba(239,68,68,.06)' } : undefined}>
            <td>
                <div style={{ fontWeight: 600 }}>{mb.email}</div>
                <div style={{ fontSize: 11, color: 'var(--c-text-4)' }}>
                    {mb.mailboxName || <em>(sin nombre)</em>}
                    {mb.emailType === 'forward_only' && ' · reenvío'}
                    {mb.dhStatus === 'disabled' && ' · deshabilitado'}
                    {!mb.presenteEnDreamhost && ' · ya no en DreamHost'}
                </div>
            </td>
            <td>
                <Pill text={CLAS_LABEL[mb.clasificacion] || mb.clasificacion} color={clasColor} bg={clasColor + '18'} />
            </td>
            <td>
                {mb.decision === 'confirmado' && mb.empleado ? (
                    <div>
                        <div style={{ fontWeight: 600 }}>{mb.empleado.nombre}</div>
                        <Pill
                            text={mb.empleado.activo ? 'Activo' : 'BAJA'}
                            color={mb.empleado.activo ? '#10b981' : '#ef4444'}
                            bg={mb.empleado.activo ? '#ecfdf5' : '#fef2f2'}
                        />
                    </div>
                ) : mb.candidatos?.length ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {mb.candidatos.slice(0, 3).map((c) => (
                            <button
                                key={c.idEmpleado}
                                onClick={() => accion((email) => confirmar(email, c.idEmpleado), mb.email)}
                                title="Confirmar este empleado"
                                style={{
                                    textAlign: 'left', border: '1px solid var(--c-border)', borderRadius: 8,
                                    padding: '3px 8px', background: 'var(--c-card)', cursor: 'pointer', fontSize: 12,
                                }}
                            >
                                <span style={{ fontWeight: 600 }}>{c.nombre}</span>{' '}
                                <span style={{ color: c.estatus === 'activo' ? '#10b981' : '#ef4444' }}>
                                    ({c.estatus})
                                </span>{' '}
                                <Badge bg="light" text="dark">{Math.round(c.score * 100)}%</Badge>
                            </button>
                        ))}
                    </div>
                ) : (
                    <span style={{ color: 'var(--c-text-4)', fontSize: 12 }}>—</span>
                )}
            </td>
            <td>
                <Pill text={DEC_LABEL[mb.decision] || mb.decision}
                    color={mb.decision === 'confirmado' ? '#10b981' : '#6b7280'}
                    bg={mb.decision === 'confirmado' ? '#ecfdf5' : '#f3f4f6'} />
                {mb.bajaSolicitada && <div style={{ marginTop: 4 }}><Pill text="Baja solicitada" color="#ef4444" bg="#fef2f2" /></div>}
                {mb.bajaEjecutada && <div style={{ marginTop: 4 }}><Pill text="Baja ejecutada" color="#10b981" bg="#ecfdf5" /></div>}
            </td>
            <td>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {mb.decision !== 'sin_match' && (
                        <Button size="sm" variant="outline-secondary"
                            onClick={() => accion(marcarSinMatch, mb.email)}><BtnIco icon={I.x}>Sin match</BtnIco></Button>
                    )}
                    {mb.decision !== 'cuenta_area' && (
                        <Button size="sm" variant="outline-secondary"
                            title="Es un buzón de área (dueño = el área). Sale de huérfanos y se oculta del directorio."
                            onClick={() => accion(cuentaArea, mb.email)}><BtnIco icon={I.area}>Es cuenta de área</BtnIco></Button>
                    )}
                    {mb.decision !== 'pendiente' && (
                        <Button size="sm" variant="outline-secondary"
                            onClick={() => accion(reabrir, mb.email)}><BtnIco icon={I.undo}>Reabrir</BtnIco></Button>
                    )}
                    <Button size="sm" variant={mb.bajaSolicitada ? 'danger' : 'outline-danger'}
                        onClick={() => accion((email) => solicitarBaja(email, !mb.bajaSolicitada), mb.email)}>
                        <BtnIco icon={mb.bajaSolicitada ? I.undo : I.trash}>{mb.bajaSolicitada ? 'Cancelar baja' : 'Solicitar baja'}</BtnIco>
                    </Button>
                    <Button size="sm" variant={mb.tienePassword ? 'success' : 'outline-secondary'}
                        title={mb.tienePassword ? 'Contraseña guardada — ver/cambiar' : 'Guardar contraseña del buzón'}
                        onClick={() => onPassword(mb)}><BtnIco icon={I.lock}>Clave</BtnIco></Button>
                </div>
            </td>
        </tr>
    );
}
